import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ToastComponent } from './shared/components/toast/toast';
import { TOAST_POSITIONS, ToastService } from './core/services/toaster';
import { Auth } from '@core/auth/auth';
import { CommonService } from '@core/services/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, ToastComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  readonly toastService = inject(ToastService);
  readonly toastPositions = TOAST_POSITIONS;
  private readonly router = inject(Router);
  private readonly auth = inject(Auth);
  private readonly commonService = inject(CommonService);

  title = 'hrm-frontend';
  idleState = 'NOT_STARTED';
  countdown?: number = 0;
  lastPing: Date = new Date();
  private authSubscription: any;
  private idleTimeoutSubscription: any;
  private timeoutId: any;
  private timeout = 5000;
  private activityHandler: any;
  private events = ['mousemove', 'keydown', 'click', 'touchstart', 'api_activity'];

  userString = localStorage.getItem('auth_user');
  user = this.userString ? JSON.parse(this.userString) : null;

  companyTimeOut = 60 * 10;
  isFirstLogin: number = 0;

  ngOnInit(): void {
    // Subscribe to user changes so idle timeout is set up on login and torn down on logout
    this.authSubscription = this.auth.user.subscribe((user: any) => {
      const mustChangePassword = user?.must_change_password === true || user?.must_change_password === 'true' || user?.must_change_password === 1;
      if (user && user.role_type !== 'admin' && user.role_type !== 'super-admin' && !mustChangePassword) {
        this.commonService.getParametersById(user.companyID).subscribe({
          next: (resp: any) => {
            const timeoutMinutes = resp?.data?.idle_time_out ? Number(resp.data.idle_time_out) : 0;
            if (!isNaN(timeoutMinutes) && timeoutMinutes > 0) {
              this.companyTimeOut = timeoutMinutes;
              this.setupIdleTimeout(timeoutMinutes);
            } else {
              console.log('[AutoLogout] Inactivity logout disabled: No idle_time_out configured in company params.');
            }
          }
        });
      } else {
        // User logged out — clear the timer and listeners
        this.removeActivityListeners();
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      }
    });

    // Re-setup idle timeout immediately when company parameters are saved with a new value
    this.idleTimeoutSubscription = this.commonService.idleTimeoutUpdated$.subscribe((timeoutMinutes: number) => {
      this.companyTimeOut = timeoutMinutes;
      this.setupIdleTimeout(timeoutMinutes);
    });
  }

  setupIdleTimeout(timeoutMinutes: number) {
    const timeoutMs = timeoutMinutes * 60 * 1000;

    console.log(`[AutoLogout] Setup for ${timeoutMinutes} minutes`);

    // Remove old listeners (IMPORTANT)
    this.removeActivityListeners();

    // Create single handler reference
    this.activityHandler = this.resetTimer.bind(this, timeoutMs);

    // Add listeners
    this.events.forEach(event => {
      window.addEventListener(event, this.activityHandler);
    });

    // Start timer
    this.resetTimer(timeoutMs);
  }

  resetTimer(timeoutMs: number) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.autoLogout();
    }, timeoutMs);
  }

  autoLogout() {
    const isLoginPage = this.router.url === '/' || this.router.url === '/login';
    if (isLoginPage) return;

    if ((window as any).activeApiRequests > 0) {
      this.resetTimer(30 * 1000); // Check again in 30 seconds
      return;
    }

    this.auth.logout().subscribe({
      next: (resp) => {
        // Logged out on backend
      },
      error: () => undefined
    });
    this.auth.updateUser(null);
    localStorage.removeItem('default_company');
    localStorage.removeItem('selectedMenuID');
    localStorage.removeItem('activeMenuName');
    localStorage.removeItem('activeMenu');
    this.auth.loggedIn.next(false);

    if (!isLoginPage) {
      this.router.navigate(['/']);

      setTimeout(() => {
        this.toastService.error("Your session has expired due to inactivity.");
      }, 100);
    }
  }

  removeActivityListeners() {
    if (this.activityHandler) {
      this.events.forEach(event => {
        window.removeEventListener(event, this.activityHandler);
      });
    }
  }

  ngOnDestroy(): void {
    this.removeActivityListeners();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    if (this.idleTimeoutSubscription) {
      this.idleTimeoutSubscription.unsubscribe();
    }
  }
}
